import React from "react";
import { api } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { MediaSlot, TextInput, TextArea, toast } from "@/components/admin/ui";

/* ====================================================================
   Homepage Editor — full control over every public homepage section.
   Fields persist via PUT /api/admin/content (accepts arbitrary keys).
   Sections (each collapsible):
     1. Age Gate
     2. Hero
     3. Trust / Proof rail
     4. This Week's Drops (Top Shelf)
     5. Build a Flight (4 tiers)
     6. Wholesale (3 cards)
     7. Founder / Owner story
     8. Landed rail
     9. Closing CTA
    10. Footer
   ==================================================================== */

export default function Homepage() {
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [open, setOpen] = React.useState({ A: true });

  React.useEffect(() => { api.getContent().then(setForm); }, []);

  if (!form) return <div className="text-[#6e6f5f] text-sm p-8">Loading…</div>;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const togg = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  async function save() {
    setSaving(true);
    try {
      // Send the entire form back so any field added in this UI persists.
      const payload = { ...form };
      delete payload._id;
      delete payload.id;
      delete payload.updated_at;
      const next = await api.saveContent(payload);
      setForm((f) => ({ ...f, ...next }));
      setDirty(false);
      toast("Homepage saved", "ok");
    } catch (e) {
      toast(e.response?.data?.detail || "Save failed", "error");
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="homepage-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8 pb-32">
      <PageHead
        eye="HOMEPAGE EDITOR"
        title="Public Homepage Content"
        sub="Every section, every line of copy, every image. Click a section to expand. Save once, reflects on the public site instantly."
      />

      <div className="space-y-4 max-w-5xl">
        {/* 1. AGE GATE */}
        <Card open={open.A} onToggle={() => togg("A")} letter="1" title="Starting Intro / Age Gate">
          <Row>
            <F label="Eyebrow tag"><TextInput data-testid="hp-age_eye" value={form.age_eye || ""} onChange={(e) => set("age_eye", e.target.value)} placeholder="— 2HIGH · MEMBERS ACCESS —" /></F>
          </Row>
          <Row cols={2}>
            <F label="Heading"><TextInput data-testid="hp-age_h" value={form.age_h || ""} onChange={(e) => set("age_h", e.target.value)} placeholder="Welcome to" /></F>
            <F label="Heading (gold italic)"><TextInput data-testid="hp-age_h_em" value={form.age_h_em || ""} onChange={(e) => set("age_h_em", e.target.value)} placeholder="2High" /></F>
          </Row>
          <F label="Body copy"><TextArea rows={3} data-testid="hp-age_p" value={form.age_p || ""} onChange={(e) => set("age_p", e.target.value)} placeholder="You must be 21 or older to enter…" /></F>
          <Row cols={2}>
            <F label="'Yes' button"><TextInput data-testid="hp-age_yes" value={form.age_yes || ""} onChange={(e) => set("age_yes", e.target.value)} placeholder="I'm 21+ · Enter" /></F>
            <F label="'No' button"><TextInput data-testid="hp-age_no" value={form.age_no || ""} onChange={(e) => set("age_no", e.target.value)} placeholder="Exit" /></F>
          </Row>
          <F label="Legal disclaimer"><TextInput data-testid="hp-age_legal" value={form.age_legal || ""} onChange={(e) => set("age_legal", e.target.value)} /></F>
        </Card>

        {/* 2. HERO */}
        <Card open={open.B} onToggle={() => togg("B")} letter="2" title="Hero Section (top fold)">
          <F label="Eyebrow tag"><TextInput data-testid="hp-hero_eye" value={form.hero_eye || ""} onChange={(e) => set("hero_eye", e.target.value)} placeholder="— PRIVATE COLLECTION · EXCLUSIVE COLLECTION" /></F>
          <Row cols={3}>
            <F label="Headline · line 1"><TextInput data-testid="hp-hero_line1" value={form.hero_line1 || ""} onChange={(e) => set("hero_line1", e.target.value)} placeholder="Premium" /></F>
            <F label="Line 2 (gold italic)"><TextInput data-testid="hp-hero_line2" value={form.hero_line2 || ""} onChange={(e) => set("hero_line2", e.target.value)} placeholder="Indoor" /></F>
            <F label="Line 3"><TextInput data-testid="hp-hero_line3" value={form.hero_line3 || ""} onChange={(e) => set("hero_line3", e.target.value)} placeholder="Exotics" /></F>
          </Row>
          <F label="Subheadline"><TextArea rows={2} data-testid="hp-hero_sub" value={form.hero_sub || ""} onChange={(e) => set("hero_sub", e.target.value)} placeholder="WELCOME TO THE TOO HIGH DIMENSION" /></F>
          <Row cols={3}>
            <F label="CTA · Menu button"><TextInput data-testid="hp-hero_cta_menu" value={form.hero_cta_menu || ""} onChange={(e) => set("hero_cta_menu", e.target.value)} placeholder="Open Menu" /></F>
            <F label="CTA · Flight button"><TextInput data-testid="hp-hero_cta_flight" value={form.hero_cta_flight || ""} onChange={(e) => set("hero_cta_flight", e.target.value)} placeholder="Build a Flight" /></F>
            <F label="CTA · Wholesale button"><TextInput data-testid="hp-hero_cta_wholesale" value={form.hero_cta_wholesale || ""} onChange={(e) => set("hero_cta_wholesale", e.target.value)} placeholder="Wholesale Access" /></F>
          </Row>
          <Row cols={2}>
            <MediaSlot testid="hp-hero_jar_img" label="Hero image · main jar" accept="image" value={form.hero_jar_img} onChange={(url) => set("hero_jar_img", url)} uploadFn={api.uploadMedia} />
            <MediaSlot testid="hp-hero_jar_second" label="Hero image · floating accent (bud)" accept="image" value={form.hero_jar_second} onChange={(url) => set("hero_jar_second", url)} uploadFn={api.uploadMedia} />
          </Row>
        </Card>

        {/* 3. TRUST / PROOF */}
        <Card open={open.C} onToggle={() => togg("C")} letter="3" title="Trust / Proof Rail">
          <Switch label="Show this section?" testid="hp-show_trust_section" value={form.show_trust_section !== false} onChange={(v) => set("show_trust_section", v)} />
          <F label="Eyebrow"><TextInput data-testid="hp-trust_sub" value={form.trust_sub || ""} onChange={(e) => set("trust_sub", e.target.value)} placeholder="— LIVE PROOF · UNEDITED · CLIPS FROM THE LAST 7 DAYS —" /></F>
          <Row cols={2}>
            <F label="Heading"><TextInput data-testid="hp-trust_h" value={form.trust_h || ""} onChange={(e) => set("trust_h", e.target.value)} placeholder="Real customers." /></F>
            <F label="Heading (gold italic)"><TextInput data-testid="hp-trust_h_em" value={form.trust_h_em || ""} onChange={(e) => set("trust_h_em", e.target.value)} placeholder="Landed safely." /></F>
          </Row>
          <F label="Marquee strip text"><TextArea rows={2} data-testid="hp-trust_marquee" value={form.trust_marquee || ""} onChange={(e) => set("trust_marquee", e.target.value)} placeholder="★ Verified Drop · Indoor Only · Batch Traceable · Hand-cured · Members Verified · 30-Day Return ·" /></F>
          <p className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] -mt-2">Card content lives in the Testimonials / Proof tab.</p>
        </Card>

        {/* 4. THIS WEEK'S DROPS */}
        <Card open={open.D} onToggle={() => togg("D")} letter="4" title="This Week's Drops (Top Shelf)">
          <F label="Eyebrow"><TextInput data-testid="hp-drops_eye" value={form.drops_eye || ""} onChange={(e) => set("drops_eye", e.target.value)} placeholder="— THIS WEEK · TOP SHELF" /></F>
          <Row cols={3}>
            <F label="Heading · word 1"><TextInput data-testid="hp-drops_h" value={form.drops_h || ""} onChange={(e) => set("drops_h", e.target.value)} placeholder="This" /></F>
            <F label="Word 2 (gold italic)"><TextInput data-testid="hp-drops_h_em" value={form.drops_h_em || ""} onChange={(e) => set("drops_h_em", e.target.value)} placeholder="week's" /></F>
            <F label="Word 3"><TextInput data-testid="hp-drops_h2" value={form.drops_h2 || ""} onChange={(e) => set("drops_h2", e.target.value)} placeholder="drops" /></F>
          </Row>
          <F label="Lead paragraph"><TextArea rows={2} data-testid="hp-drops_lead" value={form.drops_lead || ""} onChange={(e) => set("drops_lead", e.target.value)} placeholder="Four hand-selected indoor strains, freshly cured. Preview here…" /></F>
          <F label="CTA button"><TextInput data-testid="hp-drops_cta" value={form.drops_cta || ""} onChange={(e) => set("drops_cta", e.target.value)} placeholder="View Full Menu →" /></F>
          <p className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] -mt-2">Cards pulled from Products tab where "Show on homepage" is on.</p>
        </Card>

        {/* 5. BUILD A FLIGHT */}
        <Card open={open.E} onToggle={() => togg("E")} letter="5" title="Build a Flight (4 tiers)">
          <F label="Eyebrow"><TextInput data-testid="hp-flight_eye" value={form.flight_eye || ""} onChange={(e) => set("flight_eye", e.target.value)} placeholder="— BUILD YOUR FLIGHT" /></F>
          <Row cols={3}>
            <F label="Headline · word 1"><TextInput value={form.flight_h || ""} onChange={(e) => set("flight_h", e.target.value)} placeholder="Mix it." /></F>
            <F label="Word 2 (italic)"><TextInput value={form.flight_h_em || ""} onChange={(e) => set("flight_h_em", e.target.value)} placeholder="Match it." /></F>
            <F label="Word 3"><TextInput value={form.flight_h2 || ""} onChange={(e) => set("flight_h2", e.target.value)} placeholder="Hand-built for you." /></F>
          </Row>
          <F label="Lead"><TextArea rows={2} value={form.flight_lead || ""} onChange={(e) => set("flight_lead", e.target.value)} /></F>
          {[
            { k: "quad",  d: "Tier 01 · The Quad" },
            { k: "ounce", d: "Tier 02 · The Ounce" },
            { k: "qp",    d: "Tier 03 · The QP+ (Breakdowns Hub)" },
          ].map(({ k, d }) => (
            <div key={k} className="rounded-lg bg-black/30 border border-[#22261b] p-3.5 space-y-3">
              <div className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] font-mono">{d}</div>
              <Row cols={2}>
                <F label="Label / kicker"><TextInput data-testid={`hp-flight_${k}_label`} value={form[`flight_${k}_label`] || ""} onChange={(e) => set(`flight_${k}_label`, e.target.value)} placeholder="— TIER 01 · STARTER" /></F>
                <F label="Tier name"><TextInput data-testid={`hp-flight_${k}_name`} value={form[`flight_${k}_name`] || ""} onChange={(e) => set(`flight_${k}_name`, e.target.value)} placeholder="The Quad" /></F>
              </Row>
              <F label="Meta line"><TextInput value={form[`flight_${k}_meta`] || ""} onChange={(e) => set(`flight_${k}_meta`, e.target.value)} placeholder="4 strains · eighth each" /></F>
              <F label="Description"><TextArea rows={2} value={form[`flight_${k}_desc`] || ""} onChange={(e) => set(`flight_${k}_desc`, e.target.value)} /></F>
              {k === "qp" && (
                <F label="Button text (CTA line)"><TextInput value={form.flight_qp_cta || ""} onChange={(e) => set("flight_qp_cta", e.target.value)} placeholder="Breakdowns Hub →" /></F>
              )}
            </div>
          ))}
        </Card>

        {/* 6. WHOLESALE */}
        <Card open={open.F} onToggle={() => togg("F")} letter="6" title="Wholesale Section (3 cards)">
          <Switch label="Show this section?" testid="hp-show_wholesale_section" value={form.show_wholesale_section !== false} onChange={(v) => set("show_wholesale_section", v)} />
          <F label="Eyebrow"><TextInput value={form.wholesale_eye || ""} onChange={(e) => set("wholesale_eye", e.target.value)} placeholder="— QUIET WHOLESALE" /></F>
          <Row cols={3}>
            <F label="Headline · word 1"><TextInput value={form.wholesale_h || ""} onChange={(e) => set("wholesale_h", e.target.value)} placeholder="For the" /></F>
            <F label="Word 2 (italic)"><TextInput value={form.wholesale_h_em || ""} onChange={(e) => set("wholesale_h_em", e.target.value)} placeholder="operators" /></F>
            <F label="Word 3"><TextInput value={form.wholesale_h2 || ""} onChange={(e) => set("wholesale_h2", e.target.value)} placeholder="moving units." /></F>
          </Row>
          <F label="Lead"><TextArea rows={2} value={form.wholesale_lead || ""} onChange={(e) => set("wholesale_lead", e.target.value)} /></F>
          {[
            { k: "qp", d: "Bulk 01 · QP" },
            { k: "hp", d: "Bulk 02 · HP" },
            { k: "lb", d: "Bulk 03 · LB" },
          ].map(({ k, d }) => (
            <div key={k} className="rounded-lg bg-black/30 border border-[#22261b] p-3.5 space-y-3">
              <div className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] font-mono">{d}</div>
              <Row cols={2}>
                <F label="Label / kicker"><TextInput value={form[`wholesale_${k}_label`] || ""} onChange={(e) => set(`wholesale_${k}_label`, e.target.value)} /></F>
                <F label="Card name"><TextInput value={form[`wholesale_${k}_name`] || ""} onChange={(e) => set(`wholesale_${k}_name`, e.target.value)} /></F>
              </Row>
              <F label="Meta line"><TextInput value={form[`wholesale_${k}_meta`] || ""} onChange={(e) => set(`wholesale_${k}_meta`, e.target.value)} /></F>
              <F label="Description"><TextArea rows={2} value={form[`wholesale_${k}_desc`] || ""} onChange={(e) => set(`wholesale_${k}_desc`, e.target.value)} /></F>
              <MediaSlot testid={`hp-wh_${k}_img`} label={`Card image · ${d}`} accept="image" value={form[`wholesale_${k}_img`]} onChange={(url) => set(`wholesale_${k}_img`, url)} uploadFn={api.uploadMedia} />
            </div>
          ))}
          <F label="Bottom CTA button"><TextInput value={form.wholesale_cta || ""} onChange={(e) => set("wholesale_cta", e.target.value)} placeholder="View Wholesale Menu →" /></F>
        </Card>

        {/* 7. PROMO + HOW TO ORDER (formerly Founder/Owner) */}
        <Card open={open.G} onToggle={() => togg("G")} letter="7" title="Promo Video + How-to-Order Tutorial">
          <Switch label="Show this section?" testid="hp-show_founder_section" value={form.show_founder_section !== false} onChange={(v) => set("show_founder_section", v)} />
          <F label="Eyebrow"><TextInput value={form.founder_eye || ""} onChange={(e) => set("founder_eye", e.target.value)} placeholder="— WATCH · VERIFY · ORDER" /></F>
          <Row cols={3}>
            <F label="Headline · line 1"><TextInput value={form.founder_h || ""} onChange={(e) => set("founder_h", e.target.value)} placeholder="Three steps." /></F>
            <F label="Line 2 (italic)"><TextInput value={form.founder_h_em || ""} onChange={(e) => set("founder_h_em", e.target.value)} placeholder="One private line." /></F>
            <F label="Line 3"><TextInput value={form.founder_h2 || ""} onChange={(e) => set("founder_h2", e.target.value)} placeholder="Built for serious members." /></F>
          </Row>
          <Row cols={2}>
            <MediaSlot
              testid="hp-founder_cover"
              label="Video PREVIEW image (shown before play · with ▶ button on top)"
              accept="image"
              value={form.founder_cover}
              onChange={(url) => set("founder_cover", url)}
              uploadFn={api.uploadMedia}
            />
            <MediaSlot
              testid="hp-founder_video"
              label="Promo / tutorial video"
              accept="video"
              value={form.founder_video}
              poster={form.founder_video_poster}
              onChange={(url, poster) => set("founder_video", url) || (poster && set("founder_video_poster", poster))}
              uploadFn={api.uploadMedia}
            />
          </Row>
          <Row cols={3}>
            <F label="Caption · brand label"><TextInput value={form.founder_name || ""} onChange={(e) => set("founder_name", e.target.value)} placeholder="2High Promo" /></F>
            <F label="Video duration"><TextInput value={form.founder_duration || ""} onChange={(e) => set("founder_duration", e.target.value)} placeholder="0:42" /></F>
            <F label="Video title"><TextInput value={form.founder_title || ""} onChange={(e) => set("founder_title", e.target.value)} placeholder="How to order" /></F>
          </Row>
          {[1, 2, 3].map((n) => (
            <div key={n} className="rounded-lg bg-black/30 border border-[#22261b] p-3.5 space-y-3">
              <div className="text-[10.5px] tracking-[.18em] uppercase text-[#c9a86b] font-mono">Step {n}</div>
              <Row cols={2}>
                <F label="Title"><TextInput value={form[`step${n}_title`] || ""} onChange={(e) => set(`step${n}_title`, e.target.value)} placeholder={n === 1 ? "Verify · 21+" : n === 2 ? "Browse the menu" : "Order on Telegram"} /></F>
                <F label="Description"><TextInput value={form[`step${n}_text`] || ""} onChange={(e) => set(`step${n}_text`, e.target.value)} placeholder={n === 1 ? "Confirm your age. DM @official2high so we know you're real." : n === 2 ? "Open the menu, tap a drop, save your favorites." : "Send us your list on Telegram. We confirm, build, and drop it quietly."} /></F>
              </Row>
            </div>
          ))}
        </Card>

        {/* 8. LANDED RAIL */}
        <Card open={open.H} onToggle={() => togg("H")} letter="8" title="Landed Rail (Touchdown Proof)">
          <Switch label="Show this section?" testid="hp-show_landed_section" value={form.show_landed_section !== false} onChange={(v) => set("show_landed_section", v)} />
          <F label="Eyebrow"><TextInput value={form.landed_eye || ""} onChange={(e) => set("landed_eye", e.target.value)} placeholder="— LIVE FEED · LAST 48 HOURS" /></F>
          <Row cols={2}>
            <F label="Heading"><TextInput value={form.landed_h || ""} onChange={(e) => set("landed_h", e.target.value)} placeholder="Packages," /></F>
            <F label="Heading (italic)"><TextInput value={form.landed_h_em || ""} onChange={(e) => set("landed_h_em", e.target.value)} placeholder="landed." /></F>
          </Row>
          <p className="text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] -mt-2">Photos managed in Testimonials / Proof tab (kind = landed photo).</p>
        </Card>

        {/* 9. CLOSING CTA */}
        <Card open={open.I} onToggle={() => togg("I")} letter="9" title="Closing CTA">
          <F label="Eyebrow"><TextInput value={form.closing_eye || ""} onChange={(e) => set("closing_eye", e.target.value)} placeholder="— FINAL CALL · MEMBERS ACCESS" /></F>
          <Row cols={2}>
            <F label="Heading"><TextInput value={form.closing_h || ""} onChange={(e) => set("closing_h", e.target.value)} placeholder="Ready to get on the" /></F>
            <F label="Heading (italic)"><TextInput value={form.closing_h_em || ""} onChange={(e) => set("closing_h_em", e.target.value)} placeholder="private shelf?" /></F>
          </Row>
          <F label="Body"><TextArea rows={2} value={form.closing_p || ""} onChange={(e) => set("closing_p", e.target.value)} /></F>
          <Row cols={3}>
            <F label="Telegram CTA"><TextInput value={form.closing_cta_tg || ""} onChange={(e) => set("closing_cta_tg", e.target.value)} placeholder="Open Telegram" /></F>
            <F label="Menu CTA"><TextInput value={form.closing_cta_menu || ""} onChange={(e) => set("closing_cta_menu", e.target.value)} placeholder="Browse Full Menu" /></F>
            <F label="Contact CTA"><TextInput value={form.closing_cta_contact || ""} onChange={(e) => set("closing_cta_contact", e.target.value)} placeholder="Contact 2High" /></F>
          </Row>
        </Card>

        {/* 10. FOOTER */}
        <Card open={open.J} onToggle={() => togg("J")} letter="10" title="Footer">
          <F label="Footer tagline"><TextInput value={form.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} placeholder="2HIGH · MEMBERS LOFT · PRIVATE COLLECTION · 21+ · CRAFTED INDOOR" /></F>
          <F label="Footer legal line"><TextInput value={form.footer_legal || ""} onChange={(e) => set("footer_legal", e.target.value)} placeholder="© 2026 · NOT FOR RESALE · ENJOY RESPONSIBLY" /></F>
        </Card>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:left-[240px] px-5 py-3 bg-[#10120c]/95 backdrop-blur border-t border-[#22261b] flex items-center justify-between gap-3">
        <div className="text-[11px] font-mono tracking-[.06em] text-[#6e6f5f]">
          {dirty ? <span className="text-[#c9a86b]">● Unsaved changes</span> : <span className="text-[#5fa15a]">● All saved</span>}
        </div>
        <button
          data-testid="hp-save"
          onClick={save}
          disabled={saving || !dirty}
          className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50"
          style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
        >{saving ? "Saving…" : "Save Homepage"}</button>
      </div>
    </div>
  );
}

function Card({ letter, title, children, open, onToggle }) {
  return (
    <section className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden">
      <button
        type="button"
        data-testid={`hp-card-toggle-${letter}`}
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-5 py-4 hover:bg-[#181b13] transition"
      >
        <span className="w-[24px] h-[24px] rounded-md grid place-items-center text-[12px] font-bold flex-shrink-0" style={{ background: "#c9a86b", color: "#1a1407" }}>{letter}</span>
        <h3 className="text-[12.5px] tracking-[.16em] uppercase text-[#a9aa9a] m-0 flex-1 text-left">{title}</h3>
        <span className="text-[#a9aa9a] text-[14px]">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-[#22261b] pt-5">{children}</div>}
    </section>
  );
}

function Row({ children, cols = 1 }) {
  const c = cols === 3 ? "md:grid-cols-3" : cols === 2 ? "md:grid-cols-2" : "";
  return <div className={`grid grid-cols-1 ${c} gap-4`}>{children}</div>;
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Switch({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between bg-black/30 border border-[#22261b] rounded-lg px-3.5 py-2.5 cursor-pointer">
      <span className="text-[11px] tracking-[.16em] uppercase text-[#a9aa9a]">{label}</span>
      <button
        type="button"
        data-testid={testid}
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition flex items-center px-0.5 ${value ? "bg-[#c9a86b] justify-end" : "bg-[#22261b] justify-start"}`}
      >
        <span className="block w-5 h-5 rounded-full bg-white transition" />
      </button>
    </label>
  );
}
